import React, { useCallback, useEffect, useState } from 'react';
import {
  catalogRewardsDelete, catalogRewardsList, catalogRewardsUpsert, catalogTranslateReward, CatalogRewardGroup,
} from '../services/apiService';

/**
 * Master Admin → Standard rewards (2026-10-15). Suggested to new families in the setup; one row per
 * language like the standard tasks. Editing here never changes rewards families already have —
 * only future setups. "Translate to all languages" fills the other languages from English.
 */
const LANGS = ['en', 'da', 'sv', 'de', 'nb', 'fr', 'fi', 'es', 'fo', 'is', 'pl'];

type Edit = {
  group_id?: string;
  group_key: string;
  language_code: string;
  title: string;
  description: string;
  target_type: 'individual' | 'family';
  points_required: number;
  starter_checked: boolean;
  sort_order: number;
};

const empty = (): Edit => ({ group_key: '', language_code: 'en', title: '', description: '', target_type: 'individual', points_required: 25, starter_checked: false, sort_order: 0 });

const input = 'w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600';

const GlobalCatalogRewards: React.FC = () => {
  const [groups, setGroups] = useState<CatalogRewardGroup[]>([]);
  const [editing, setEditing] = useState<Edit | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setGroups(await catalogRewardsList()); } catch (e: any) { setMessage(e?.message || 'Could not load'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const textFor = (g: CatalogRewardGroup, lang: string) => g.reward_templates.find((t) => t.language_code === lang);
  const open = (g: CatalogRewardGroup, lang = 'en') => {
    const t = textFor(g, lang);
    setEditing({
      group_id: g.id, group_key: g.group_key, language_code: lang, title: t?.title || '', description: t?.description || '',
      target_type: g.target_type, points_required: g.points_required, starter_checked: g.starter_checked, sort_order: g.sort_order,
    });
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await catalogRewardsUpsert({ ...editing });
      setMessage('Saved. Families keep the rewards they already have; future setups get this version.');
      setEditing(null);
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const translate = async (g: CatalogRewardGroup) => {
    setBusy(true);
    try {
      const r = await catalogTranslateReward(g.id);
      setMessage(`Translated: ${r.applied.join(', ') || '—'}${r.failed.length ? ` · failed: ${r.failed.join(', ')}` : ''}`);
      await load();
    } catch (e: any) {
      setMessage(e?.message || 'Could not translate');
    } finally {
      setBusy(false);
    }
  };

  // Every reward that lacks a language: one Claude call per reward and missing language.
  const translateMissing = async () => {
    const todo = groups.filter((g) => textFor(g, 'en') && LANGS.some((l) => !textFor(g, l)));
    if (!todo.length) { setMessage('Every standard reward already has all languages.'); return; }
    if (!window.confirm(`Translate ${todo.length} rewards into the missing languages?`)) return;
    setBusy(true);
    const failed: string[] = [];
    for (let i = 0; i < todo.length; i++) {
      setMessage(`Translating… ${i} of ${todo.length}`);
      try {
        const r = await catalogTranslateReward(todo[i].id);
        if (r.failed.length) failed.push(`${textFor(todo[i], 'en')?.title} (${r.failed.join(', ')})`);
      } catch (e: any) { failed.push(`${textFor(todo[i], 'en')?.title}: ${e?.message}`); }
    }
    setMessage(`Done (marked "needs review").${failed.length ? ` Failed: ${failed.join('; ')}` : ''}`);
    setBusy(false);
    await load();
  };

  const hide = async (g: CatalogRewardGroup) => {
    if (!window.confirm(`Hide "${textFor(g, 'en')?.title}" from future setups? Families keep their own copies.`)) return;
    await catalogRewardsDelete(g.id);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Standard rewards</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Suggested in the new family setup. "Each child" rewards are created once per child; "Whole family" once.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="px-4 py-2 rounded border" disabled={busy} onClick={translateMissing}>Translate all missing</button>
          <button type="button" className="px-4 py-2 rounded bg-blue-600 text-white" onClick={() => setEditing(empty())}>+ New standard reward</button>
        </div>
      </div>
      {message && <p className="text-sm rounded bg-gray-100 dark:bg-gray-800 px-3 py-2">{message}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b dark:border-gray-700">
            <th className="py-2">Reward (English)</th><th>For</th><th>★</th><th>Ticked in setup</th><th>Languages</th><th />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const review = g.reward_templates.filter((t) => t.needs_review).length;
            return (
              <tr key={g.id} className="border-b dark:border-gray-800">
                <td className="py-2">{textFor(g, 'en')?.title || g.group_key}</td>
                <td>{g.target_type === 'family' ? 'Whole family' : 'Each child'}</td>
                <td>{g.points_required}</td>
                <td>{g.starter_checked ? '✓' : ''}</td>
                <td>
                  {g.reward_templates.length}/{LANGS.length}
                  {review > 0 && <span className="ml-2 rounded bg-amber-100 text-amber-900 px-2 py-0.5">{review} to review</span>}
                </td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  <button type="button" className="underline" onClick={() => open(g)}>Edit</button>
                  <button type="button" className="underline" disabled={busy} onClick={() => translate(g)}>Translate to all languages</button>
                  <button type="button" className="underline text-red-700" onClick={() => hide(g)}>Hide</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold">{editing.group_id ? 'Edit standard reward' : 'New standard reward'}</h3>
            {editing.group_id && (
              <label className="block text-sm">Language
                <select className={input} value={editing.language_code} onChange={(e) => {
                  const g = groups.find((x) => x.id === editing.group_id);
                  if (g) open(g, e.target.value);
                }}>
                  {LANGS.map((l) => <option key={l} value={l}>{l}{groups.find((x) => x.id === editing.group_id)?.reward_templates.some((t) => t.language_code === l) ? '' : ' (missing)'}</option>)}
                </select>
              </label>
            )}
            <label className="block text-sm">Title<input className={input} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></label>
            <label className="block text-sm">Description (optional)<textarea className={input} rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">For
                <select className={input} value={editing.target_type} onChange={(e) => setEditing({ ...editing, target_type: e.target.value as Edit['target_type'] })}>
                  <option value="individual">Each child</option>
                  <option value="family">Whole family</option>
                </select>
              </label>
              <label className="block text-sm">Stars needed<input type="number" min={1} className={input} value={editing.points_required}
                onChange={(e) => setEditing({ ...editing, points_required: Math.max(1, parseInt(e.target.value, 10) || 1) })} /></label>
              <label className="block text-sm">Order<input type="number" className={input} value={editing.sort_order}
                onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })} /></label>
              <label className="flex items-center gap-2 text-sm mt-6">
                <input type="checkbox" checked={editing.starter_checked} onChange={(e) => setEditing({ ...editing, starter_checked: e.target.checked })} />
                Ticked by default in setup
              </label>
            </div>
            <p className="text-xs text-gray-500">Stars, "for" and "ticked" are shared by all languages. Changing the English text marks the other languages for review.</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded border" onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50" disabled={busy || !editing.title.trim()} onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalCatalogRewards;
